<template>
    <div>
        <limitless-page-header>
            <template #header-elements>
                <limitless-page-header-button @click="$router.push({ name: 'address_lists.list' })" icon="icon-circle-left2">Tillbaka till lista</limitless-page-header-button>
            </template>
        </limitless-page-header>

        <div v-if="!initialized && busy_loading" class="content">
            <i class="icon-spinner2 spinner"></i>
        </div>
        <div v-else-if="!not_found && list" class="content" style="padding-bottom: 6rem;">
            <div class="card">
                <div class="card-body">
                    <form>
                        <ValidationObserver ref="listform">
                            <input-with-validation
                                v-model="list.name"
                                rules="required"
                                label="Namn"
                            ></input-with-validation>
                        </ValidationObserver>
                    </form>
                </div>
                <div class="card-footer text-right">
                    <limitless-button ref="btnsavelist" type="button" @click="saveList" icon="icon-floppy-disk" :busy="busy_saving">Spara</limitless-button>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <csv-import
                        @csv:imported="$refs.recipientstable.reload()"
                        :start-header="allowedColumns.join(',')"
                        :allowed-columns="allowedColumns"
                        :api-endpoint-url="'/vue/address_lists/' + this.list.id + '/import'"
                    ></csv-import>
                </div>
            </div>

            <rednax-datatable ref="recipientstable" :url="'/vue/address_lists/' + list.id + '/addresses'" :columns="table_columns" :filter="table_filter" sort-by="recipient_name" sort-order="asc" :per-page="100">
                <div class="row">
                    <div class="col-lg-3">
                        <input type="text" v-model="table_filter.search" class="form-control" placeholder="Sök...">
                    </div>
                </div>
            </rednax-datatable>
        </div> <!-- /content -->
    </div>
</template>

<script>
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import LimitlessPageHeaderButton from '@components/Layout/partials/LimitlessPageHeaderButton';
import RednaxDatatable from '@components/Datatable/RednaxDatatableComponent';
import InputWithValidation from '@components/Form/InputWithValidation.vue';
import apiClient from '@services/ApiClient';
import LimitlessButton from '@components/Limitless/LimitlessButton.vue';
import getError from '@services/ErrorHelper';
import CsvImport from '@components/DataTools/CsvImport.vue';

export default {
    name: 'AddressListEdit',
    components: {
        CsvImport,
        LimitlessButton,
        InputWithValidation,
        LimitlessPageHeader,
        LimitlessPageHeaderButton,
        RednaxDatatable,
    },
    props: {
        id: {
            type: [String, Number],
            required: true,
        },
    },
    data: () => ({
        not_found: false,
        initialized: false,
        busy_loading: true,
        busy_saving: false,
        busy_deleting: false,
        list: null,
        table_filter: {
            search: '',
        },
        table_columns: [
            {
                data: 'recipient_name',
                header: 'Name',
                sortable: true,
            },
            {
                data: 'recipient_street1',
                header: 'Street 1',
                sortable: true,
            },
            {
                data: 'recipient_city',
                header: 'Street 2',
                sortable: true,
            },
            {
                data: 'recipient_zip',
                header: 'Zip',
                sortable: true,
            },
            {
                data: 'recipient_country',
                header: 'Country',
                sortable: true,
            },
            {
                data: 'contact_name',
                header: 'Kontaktperson',
                sortable: true,
            },
            {
                data: 'contact_email',
                header: 'Kontakt Email',
                sortable: true,
            },
            {
                data: 'contact_phone',
                header: 'Kontakttelefon',
                sortable: true,
            },
            {
                data: 'service',
                header: 'Service',
                sortable: true,
            },
            {
                data: 'reference',
                header: 'Reference',
                sortable: true,
            },
        ],
        allowedColumns: [
            'recipient_name',
            'recipient_street1',
            'recipient_street2',
            'recipient_city',
            'recipient_zip',
            'recipient_country',
            'contact_name',
            'contact_phone',
            'contact_email',
            'service',
            'reference',
            'door_code',

            'top_text1',
            'top_text2',
            'top_text3',

            'top_info1',
            'top_info2',
            'top_info3',
            'top_info4',
            'top_info5',
            'top_info6',
            'top_info7',
            'top_info8',

            'free_text1',
            'free_text2',
            'free_text3',
            'free_text4',

            'content',
        ],
    }),
    mounted() {
        this.init();
    },
    watch: {
        id() {
            this.init();
        },
    },
    methods: {
        init() {
            this.getList();
        },
        getList() {
            this.busy_loading = true;
            apiClient.get('/vue/address_lists/' + this.id)
                .then((response) => {
                    this.list = response.data;
                })
                .catch((error) => {
                    const { status } = error.response;
                    if (status === 404) {
                        this.not_found = true;
                    }
                })
                .finally(() => {
                    this.initialized = true;
                    this.busy_loading = false;
                });
        },
        async saveList() {
            const isValid = await this.$refs.listform.validate();
            if (isValid) {
                this.busy_saving = true;
                await apiClient.post('/vue/address_lists/' + this.list.id, { ...this.list })
                    .then((response) => {
                        if (parseInt(this.id, 10) !== parseInt(response.data.id, 10)) {
                            this.$router.push({ name: 'address_lists.edit', params: { id: response.data.id } });
                        }
                        this.list = response.data;
                        this.$refs.btnsavelist.triggerSaved();
                    }).catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.listform.setErrors(parsederror);
                        }
                        this.$refs.btnsavelist.triggerFailed();
                    }).finally(() => {
                        this.busy_saving = false;
                    });
            } else {
                this.$refs.btnsavelist.triggerFailed();
            }
        },
    },
};

</script>
